import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Bold, Italic, List, ListOrdered } from "lucide-react";
import { Button } from "./ui/button";
import { Throbber } from "./ui/throbber";
import { markdownToEmailHtml, stripEmphasisMarkers } from "../lib/markdown";
import {
  decorateCitationTokens,
  editorHtmlToMarkdown,
  selectionToClipboardFlavors,
} from "../lib/editorHtml";
import { stripCitationMarkers } from "../lib/utils";

import "../styles/editor.css"

interface MarkdownAnswerEditorProps {
  /** Raw answer text (constrained markdown, citation markers included). */
  initialText: string;
  /** Disables editing and shows the primary button's spinner. */
  saving?: boolean;
  /**
   * The one stable action button (standard primary styling — the gradient is
   * reserved for AI-magic actions). Clean state: label `primaryLabel`, fired
   * with `null` (use the persisted answer). Dirty state: label
   * `primaryEditingLabel` ("Speichern & …"), fired with the serialized
   * markdown — the parent saves AND performs the action in one click, and the
   * button never moves, so switching modes causes no visual flutter. While
   * editing, an Abbrechen button appears to its LEFT that reverts in place.
   */
  primaryLabel: ReactNode;
  primaryEditingLabel: ReactNode;
  onPrimary: (markdown: string | null) => void;
  /**
   * Grays out the primary button in the CLEAN state only. Once the text is
   * edited the button is enabled again, so a host whose main action is
   * unavailable can hand the edit to a fallback (e.g. save & copy) instead of
   * stranding it.
   */
  primaryDisabled?: boolean;
  /** Small muted line under the action row (e.g. why the primary is grayed out). */
  primaryHint?: ReactNode;
  /**
   * Additional action buttons (e.g. Venus's insert/send), rendered left of
   * the primary button only while NOT editing — an edited-but-unsaved answer
   * must not be sendable.
   */
  extraActions?: ReactNode;
  /**
   * Notifies the parent when the editor is "in editing" — focused or holding
   * unsaved edits. Clicking into the text already counts as editing; the
   * state clears on blur without changes, discard, or save.
   */
  onEditingChange?: (editing: boolean) => void;
  /** Called with the citation index when an inline citation chip is clicked. */
  onCitationClick?: (index: number) => void;
  /**
   * Rendered right-aligned in the toolbar row (e.g. translate/version
   * controls) so hosts can share the row instead of stacking their own.
   */
  toolbarExtra?: ReactNode;
  /** Rendered between the editable area and the action row (not editable content). */
  belowContent?: ReactNode;
  /**
   * output.single-linebreak (#379): the tenant stores single-"\n" paragraph
   * separators. Serialized markdown (save AND both copy paths) is collapsed
   * to that format — without it an edit that adds a paragraph would put
   * "\n\n" on the clipboard even though the backend collapses the save. The
   * editor keeps the native paragraph block model either way (lists need it)
   * and only drops the paragraph GAP, see .markdown-answer-tight.
   */
  singleLineBreak?: boolean;
  /**
   * Told on every edit, with a way to serialize the current text (markdown, as the
   * primary action would hand it over). For a host that saves on its own (Venus
   * autosave): serializing only when it saves keeps typing cheap.
   */
  onContentEdit?: (serialize: () => string) => void;
  /**
   * The text the host last saved from this editor. It becomes what "Abbrechen" returns
   * to, and the editor is clean again while its text still equals it.
   */
  savedText?: string | null;
  /** Focus left the editor (its toolbar and buttons count as inside). */
  onLeave?: () => void;
}

// Whether the caret sits inside a list item of this editor. Starts from the
// anchor's element (a text node's parent) so a caret inside <strong>/<em>
// counts too.
const caretInListItem = (editor: HTMLElement): boolean => {
  const anchor = window.getSelection()?.anchorNode;
  const start = anchor?.nodeType === Node.TEXT_NODE ? anchor.parentElement : anchor;
  return (
    start instanceof HTMLElement && editor.contains(start) && !!start.closest("li")
  );
};

/**
 * Always-on WYSIWYG surface for the proposed answer: the stored markdown is
 * converted to HTML for editing and serialized back to constrained markdown
 * on save (editorHtmlToMarkdown reads DOM structure, so formatting survives,
 * unlike the old innerText-based editing). The toolbar drives the browser's
 * native bold/italic/list commands, which toggle.
 *
 * Citation markers ("[ref:N]") render as inert chip tokens — deletable as a
 * whole, not editable inside — and serialize back to their original marker,
 * so citations survive edits unless deliberately removed.
 *
 * Shared between Venus (AgentProposedAnswer) and Neptune (ProposedAnswerTab).
 */
export function MarkdownAnswerEditor({
  initialText,
  saving = false,
  primaryLabel,
  primaryEditingLabel,
  onPrimary,
  primaryDisabled,
  primaryHint,
  extraActions,
  onEditingChange,
  onCitationClick,
  toolbarExtra,
  belowContent,
  singleLineBreak = false,
  onContentEdit,
  savedText = null,
  onLeave,
}: MarkdownAnswerEditorProps) {
  const { t } = useTranslation();
  const editorRef = useRef<HTMLDivElement | null>(null);
  // Read via a ref inside the mount-only clipboard listeners.
  const singleLineBreakRef = useRef(singleLineBreak);
  singleLineBreakRef.current = singleLineBreak;
  const collapseIfSingleLineBreak = useCallback(
    (markdown: string) =>
      singleLineBreakRef.current ? markdown.replace(/\n\s*\n/g, "\n") : markdown,
    [],
  );
  const [dirty, setDirty] = useState(false);
  const [focused, setFocused] = useState(false);
  // Which formatting commands apply at the current caret/selection — drives
  // the pressed look on the toolbar so it is obvious a state can be toggled
  // OFF (e.g. the list button lights up while inside a list).
  const [activeCommands, setActiveCommands] = useState<Record<string, boolean>>({});

  const editing = focused || dirty;
  useEffect(() => {
    onEditingChange?.(editing);
  }, [editing, onEditingChange]);

  // The editor is uncontrolled after mount: the DOM is seeded ONCE from the
  // markdown captured at mount time and prop changes never touch it again.
  // Background message refetches (grounding pass rewriting markers, polling)
  // update the answer prop mid-edit; re-applying innerHTML on such a change
  // would wipe the operator's in-progress edits. A revision switch remounts
  // via the parent's key, which is the one legitimate reset.
  // Safe innerHTML: markdownToEmailHtml escapes ALL input before adding
  // markup, so this is our own generated markup, never raw user/model HTML.
  const [initialHtml] = useState(() =>
    decorateCitationTokens(markdownToEmailHtml(initialText)),
  );

  // Seed the DOM once — no focus stealing: the always-on editor mounts on
  // page load.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    el.innerHTML = initialHtml;
  }, [initialHtml]);

  // What "Abbrechen" restores: the seeded text, or the host's last save of it.
  const baselineHtml = useRef(initialHtml);
  const serialize = useCallback(() => {
    const el = editorRef.current;
    return el ? collapseIfSingleLineBreak(editorHtmlToMarkdown(el)) : "";
  }, [collapseIfSingleLineBreak]);
  // Read inside the mount-only listeners.
  const onContentEditRef = useRef(onContentEdit);
  onContentEditRef.current = onContentEdit;
  const markEdited = useCallback(() => {
    setDirty(true);
    // Bound to the element, not the ref: a host may serialize after the editor unmounted
    // (saving what is pending as the ticket closes), when the ref is already null.
    const el = editorRef.current;
    if (el) onContentEditRef.current?.(() => collapseIfSingleLineBreak(editorHtmlToMarkdown(el)));
  }, [collapseIfSingleLineBreak]);
  useEffect(() => {
    if (savedText === null) return;
    baselineHtml.current = decorateCitationTokens(markdownToEmailHtml(savedText));
    if (serialize() === savedText) setDirty(false);
  }, [savedText, serialize]);

  // Block editing intents outside the subset (Ctrl+U underline, drops of
  // rich content, …) so the editor never SHOWS formatting that the save
  // would silently discard. Allowlist over blocklist: unknown future
  // inputTypes fail closed. Registered natively — React's synthetic
  // onBeforeInput does not expose inputType reliably, and execCommand
  // (our toolbar) does not fire beforeinput at all, so it is unaffected.
  // insertCompositionText (IME) is not cancelable per spec and must pass.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const allowed = new Set([
      "insertText",
      "insertParagraph",
      "insertLineBreak",
      "insertCompositionText",
      "insertReplacementText",
      "insertFromComposition",
      "deleteContentBackward",
      "deleteContentForward",
      "deleteByCut",
      "deleteWordBackward",
      "deleteWordForward",
      "deleteSoftLineBackward",
      "deleteHardLineBackward",
      "deleteByDrag",
      "historyUndo",
      "historyRedo",
      "formatBold",
      "formatItalic",
      "insertOrderedList",
      "insertUnorderedList",
    ]);
    const onBeforeInput = (e: InputEvent) => {
      // Shift+Enter inside a list item: the subset knows only flat one-line
      // items, so the serializer turns a <br> in an <li> into two items. Do
      // that visibly — start the next item — instead of showing a wrapped
      // bullet that save and copy silently split (#556). Outside a list a
      // line break is representable and stays untouched.
      if (e.inputType === "insertLineBreak" && caretInListItem(el)) {
        e.preventDefault();
        document.execCommand("insertParagraph");
        markEdited();
        return;
      }
      if (e.inputType && !allowed.has(e.inputType)) e.preventDefault();
    };
    // Paste as plain text: rich clipboard markup (Word bold as styled spans,
    // colors, images) would render in the editor but not survive the subset
    // serializer — inserting the text flavor keeps what-you-see truthful.
    // insertText via execCommand keeps the native undo stack intact.
    const onPaste = (e: ClipboardEvent) => {
      e.preventDefault();
      let text = e.clipboardData?.getData("text/plain");
      // Single-linebreak tenants: pasted blank lines become single breaks so
      // the pasted text matches the tenant's stored format immediately.
      if (text && singleLineBreakRef.current) text = text.replace(/\n\s*\n/g, "\n");
      if (text) document.execCommand("insertText", false, text);
      markEdited();
    };
    // Manual selection-copy: replace the browser's DOM serialization with the
    // same flavors the copy button produces — the selection fragment goes
    // through the DOM→markdown serializer, so line-break semantics match the
    // stored answer (Kramer/Advasco line shifts, EXTENSION-AA), citation
    // chips are dropped, and rich targets still get real markup.
    const onCopy = (e: ClipboardEvent) => {
      const selection = window.getSelection();
      if (!selection || !e.clipboardData) return;
      const flavors = selectionToClipboardFlavors(selection);
      if (!flavors) return;
      const markdown = collapseIfSingleLineBreak(stripCitationMarkers(flavors.markdown));
      if (!markdown.trim()) return;
      e.preventDefault();
      e.clipboardData.setData("text/plain", stripEmphasisMarkers(markdown));
      e.clipboardData.setData("text/html", markdownToEmailHtml(markdown));
    };
    el.addEventListener("beforeinput", onBeforeInput);
    el.addEventListener("paste", onPaste);
    el.addEventListener("copy", onCopy);
    return () => {
      el.removeEventListener("beforeinput", onBeforeInput);
      el.removeEventListener("paste", onPaste);
      el.removeEventListener("copy", onCopy);
    };
  }, [markEdited]);

  // Read the formatting state at the selection via queryCommandState (the
  // read companion of execCommand). Only meaningful while the selection is
  // inside the editor; cleared otherwise so the buttons don't stay lit.
  const refreshActiveCommands = useCallback(() => {
    const el = editorRef.current;
    const selection = window.getSelection();
    const inEditor = !!el && !!selection?.anchorNode && el.contains(selection.anchorNode);
    let next: Record<string, boolean> = {};
    if (inEditor) {
      try {
        next = {
          bold: document.queryCommandState("bold"),
          italic: document.queryCommandState("italic"),
          insertUnorderedList: document.queryCommandState("insertUnorderedList"),
          insertOrderedList: document.queryCommandState("insertOrderedList"),
        };
      } catch {
        next = {};
      }
    }
    setActiveCommands((prev) => {
      const keys = ["bold", "italic", "insertUnorderedList", "insertOrderedList"];
      return keys.every((k) => !!prev[k] === !!next[k]) ? prev : next;
    });
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", refreshActiveCommands);
    return () => document.removeEventListener("selectionchange", refreshActiveCommands);
  }, [refreshActiveCommands]);

  const exec = useCallback(
    (command: string) => {
      editorRef.current?.focus();
      document.execCommand(command);
      markEdited();
      refreshActiveCommands();
    },
    [refreshActiveCommands, markEdited],
  );

  // Discard edits: restore the seeded content and leave the editing state
  // (there is no "closed" state to return to, so blur is the exit).
  const revert = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    el.innerHTML = baselineHtml.current;
    setDirty(false);
    el.blur();
    setFocused(false);
  }, []);

  // Clean: act on the persisted answer (null). Dirty: hand the serialized
  // markdown over so the parent saves and acts in the same click — the
  // parent should perform clipboard work synchronously (user gesture) and
  // may run the save in the background.
  const handlePrimary = useCallback(() => {
    if (!dirty) {
      onPrimary(null);
      return;
    }
    const el = editorRef.current;
    if (!el) return;
    const markdown = collapseIfSingleLineBreak(editorHtmlToMarkdown(el));
    if (!markdown.trim()) return;
    onPrimary(markdown);
  }, [dirty, onPrimary, collapseIfSingleLineBreak]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        e.preventDefault();
        revert();
      }
    },
    [revert],
  );

  // Citation chips are inert atoms; a click on one is the "show me the
  // source" gesture, routed to the parent with the chip's first index.
  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!onCitationClick) return;
      const token = (e.target as HTMLElement).closest?.(".citation-token");
      const index = parseInt(token?.textContent ?? "", 10);
      if (!isNaN(index)) onCitationClick(index);
    },
    [onCitationClick],
  );

  const toolbarButton = (
    label: string,
    icon: ReactNode,
    command: string,
  ) => (
    <button
      type="button"
      className={`h-7 w-7 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
        activeCommands[command]
          ? "bg-muted text-foreground"
          : "hover:bg-muted/50 text-muted-foreground hover:text-foreground"
      }`}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => exec(command)}
      title={label}
      aria-label={label}
      aria-pressed={!!activeCommands[command]}
    >
      {icon}
    </button>
  );

  return (
    <div
      className="space-y-1.5"
      // Focus tracking on the wrapper, but only focus INSIDE the editable
      // area starts "editing" — a click on the copy button also focuses it
      // and must not flip the mode. Once editing, toolbar/action clicks keep
      // it alive; only leaving the whole component clears it.
      onFocus={(e) => {
        if (editorRef.current?.contains(e.target as Node)) setFocused(true);
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setFocused(false);
          onLeave?.();
        }
      }}
    >
      {/* One flat style for the whole bar: the formatting buttons match the
          quiet ghost look of the translate/version triggers on the right —
          the old framed pill was a leftover from when they stood alone. */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 w-fit">
          {toolbarButton(t("markdownEditor.bold", "Fett (Strg+B)"), <Bold className="h-4 w-4" />, "bold")}
          {toolbarButton(t("markdownEditor.italic", "Kursiv (Strg+I)"), <Italic className="h-4 w-4" />, "italic")}
          {toolbarButton(t("markdownEditor.bulletList", "Aufzählung"), <List className="h-4 w-4" />, "insertUnorderedList")}
          {toolbarButton(t("markdownEditor.numberedList", "Nummerierte Liste"), <ListOrdered className="h-4 w-4" />, "insertOrderedList")}
        </div>
        {toolbarExtra && (
          <div className="flex items-center gap-2">{toolbarExtra}</div>
        )}
      </div>
      <div
        ref={editorRef}
        contentEditable={!saving}
        suppressContentEditableWarning
        className={`markdown-answer markdown-answer-frame w-full min-h-32 bg-background px-2.5 py-1.5 text-sm leading-relaxed aria-disabled:opacity-60${
          singleLineBreak ? " markdown-answer-tight" : ""
        }`}
        aria-disabled={saving}
        onInput={markEdited}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
      />
      {/* Indented to match the editable content above, whose own px-2.5 this
          mirrors. The slot exists to continue the answer text -- the agent's
          personal signature -- so a bare child would start 10px to its left and
          read as a separate block rather than the end of the reply. */}
      {belowContent && <div className="px-2.5">{belowContent}</div>}
      {/* One stable action row: the primary button keeps its place across
          mode switches; Abbrechen slides in on its left while editing, the
          host's extra actions show only in the clean state. */}
      <div className="flex justify-end items-center gap-2">
        {!editing && extraActions}
        {editing && (
          <Button variant="outline" size="sm" onClick={revert} disabled={saving}>
            {t("markdownEditor.discard", "Abbrechen")}
          </Button>
        )}
        <Button
          size="sm"
          onClick={handlePrimary}
          disabled={saving || (!dirty && primaryDisabled)}
        >
          {saving && <Throbber className="h-4 w-4 mr-1.5" />}
          {dirty ? primaryEditingLabel : primaryLabel}
        </Button>
      </div>
      {primaryHint && !dirty && (
        <p className="text-right text-xs text-muted-foreground">{primaryHint}</p>
      )}
    </div>
  );
}
